/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react'
import { UK_AUTHORITY_NAME } from '@constants/legal'
import { useNestedTranslation, useShouldDisplayProviderScopes, usePermissionsString } from '@hooks'
import { DefaultPageLayout } from '@layouts'
import { isOnlyInfoScope } from '@utils/scope'
import useConsent, { useDataUseWithFallback } from '../../hooks'
import ConsentButtons from '../ConsentButtons'
import ConsentDataUseAccessNote from '../ConsentDataUseAccessNote'
import ConsentLegalNote from '../ConsentLegalNote'
import ConsentNotesAccordionUKEU from '../ConsentNotesAccordionUKEU'
import ConsentNotesLink from '../ConsentNotesLink'
import ConsentNotesTabsUKEU from '../ConsentNotesTabsUKEU'
import {
  ConsentPermissionsListNote,
  ConsentProviderPermissionsListNote,
  ConsentLegalNoteContent,
} from '@components'
import ConsentLayoutUKEU from '../ConsentLayoutUKEU'
import ConsentDataAccessNote from '../ConsentDataAccessNote'
import ConsentOngoingAccessNote from '../ConsentOngoingAccessNote'
import ConsentLogosShieldCheck from '../ConsentLogosShieldCheck'

function ConsentContentUKRegulated() {
  const whatDataAmISharingTranslation = useNestedTranslation('v2_consent', [
    'what_data_am_i_sharing',
  ])
  const howDoesThisWorkTranslation = useNestedTranslation('v2_consent', ['how_does_this_work'])
  const titleTranslation = useNestedTranslation('v2_consent', ['title'])
  const subtitleTranslation = useNestedTranslation('v2_consent', ['subtitle'])

  const {
    providerName,
    clientName,
    regulatoryInformation,
    scope,
    shouldDisplayNewSubtitleCopy,
    providerScope,
  } = useConsent()

  const { dataUseCopy, dataUseBase } = useDataUseWithFallback()

  const title = isOnlyInfoScope(scope)
    ? titleTranslation('access_info_scope', {
        clientName,
        providerName,
      })
    : titleTranslation('access', {
        clientName,
      })

  const subtitle = shouldDisplayNewSubtitleCopy
    ? subtitleTranslation('retrieve_data_regulated_with_data_use', {
        clientName,
        dataUse: dataUseBase,
      })
    : undefined

  const shouldDisplayProviderScopes = useShouldDisplayProviderScopes({ scope, providerScope })
  const permissionsString = usePermissionsString({ providerName, scope })

  const notesProps = {
    howDoesThisWork: (
      <>
        <div data-view="how-does-this-work-explanation">
          {howDoesThisWorkTranslation('explanation.regulated', {
            providerName,
            clientName,
          })}
          .
        </div>
        <br />
        <div data-view="regulation-information">
          <div>
            {howDoesThisWorkTranslation('encryption_info.account', {
              entityName: clientName,
            })}
            .
          </div>
          <br />
          <div>{howDoesThisWorkTranslation('login_info', { clientName })}.</div>
          <br />
          <div>
            {howDoesThisWorkTranslation('authority_info', {
              entityName: clientName,
              authorityName: UK_AUTHORITY_NAME,
            })}
            .{' '}
            <ConsentNotesLink href={regulatoryInformation!.registration_page_url}>
              FRN {regulatoryInformation!.registration_number}
            </ConsentNotesLink>
          </div>
        </div>
      </>
    ),
    whatDataAmISharing: (
      <>
        <ConsentDataAccessNote />
        <br />
        <ConsentPermissionsListNote scope={scope} />
        <br />
        <ConsentOngoingAccessNote />
        {shouldDisplayProviderScopes && (
          <>
            <div>
              {whatDataAmISharingTranslation('info.access_provider', {
                providerName,
                clientName,
              })}
              :
            </div>
            <br />
            <ConsentProviderPermissionsListNote scope={scope} providerScope={providerScope} />
            <br />
            <div data-view="sharing-disclaimer">
              {whatDataAmISharingTranslation('disclaimer.data_not_used_regulated', {
                clientName,
                permissionsString,
              })}
              .
            </div>
          </>
        )}
      </>
    ),
    howIsMyDataUsed: (
      <>
        {dataUseCopy}
        .
        <br />
        <br />
        <ConsentDataUseAccessNote />
      </>
    ),
  }

  return (
    <DefaultPageLayout title={title} subtitle={subtitle} logo={<ConsentLogosShieldCheck />}>
      <ConsentLayoutUKEU
        notesAccordion={<ConsentNotesAccordionUKEU {...notesProps} />}
        notesTabs={<ConsentNotesTabsUKEU {...notesProps} />}
        agreement={
          <>
            <ConsentButtons />
            <ConsentLegalNote>
              <ConsentLegalNoteContent
                entityName={clientName}
                tosLink={regulatoryInformation!.terms_of_service_url!}
                privacyLink={regulatoryInformation!.privacy_policy_url!}
              />
            </ConsentLegalNote>
          </>
        }
      />
    </DefaultPageLayout>
  )
}

export default ConsentContentUKRegulated
